import { useEffect, useRef, useState } from "react";
import {
  MdAccessTime,
  MdApartment,
  MdBusiness,
  MdCalendarToday,
  MdDelete,
  MdEdit,
  MdGroups,
  MdLocalPolice,
  MdOutlineFlashOn,
} from "react-icons/md";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { FieldGroup, FormModal, Input, SelectField, Textarea } from "../../components/ui";
import { HabBadge, habColor } from "../../shared/habilitations";
import HighlightText from "../../shared/HighlightText";
import { graviteColor, statutIncColor } from "../../shared/statusColors";
import { COLORS, RADIUS, SPACING } from "../../shared/theme";
import { incidentsApi, reportsApi } from "../../services/apiClient";
import { reportGenerateSuccessToastMessage } from "../reports/reportShared";
import { formatDateFr, formatDateTimeFr } from "../../shared/dateFormat";

const GRAVITE = ["Mineure", "Modérée", "Majeure", "Critique"];
const STATUT_INC = ["Détecté", "En cours d'analyse", "Notifié CNIL", "Clôturé", "Sans suite"];
const TYPE_INC = ["Accès non autorisé", "Perte/vol de données", "Divulgation accidentelle", "Ransomware / Cyberattaque", "Erreur humaine", "Défaillance technique", "Autre"];


const emptyIncident = {
  titre: "", type: "Accès non autorisé", gravite: "Modérée", statut: "Détecté",
  dateDetection: "", dateNotifCnil: "", delai72h: false, responsableId: "",
  sousTraitantConcerne: "", donneesImpactees: "", nbPersonnesConcernees: "",
  descriptionIncident: "", mesuresPrises: "", notifPersonnes: false, observations: "",
};

export default function PageIncidents({ incidents, setIncidents, personnes, sousTraitants, searchHighlight, onToast }) {
  const [showForm, setShowForm] = useState(false);
  const [reportBusyId, setReportBusyId] = useState(null);
  const highlightRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("Tous");
  const nextId = useRef(50);
  const [form, setForm] = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCheck = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));

  const openNew = () => { setEditing(null); setForm({ ...emptyIncident }); setShowForm(true); };
  const openEdit = inc => { setEditing(inc); setForm({ ...inc }); setShowForm(true); };
  const save = () => {
    if (editing) setIncidents(is => is.map(i => i.id === editing.id ? { ...form, id: editing.id } : i));
    else setIncidents(is => [...is, { ...form, id: nextId.current++ }]);
    setShowForm(false); setEditing(null); setForm(null);
  };

  const stOptions = [{ value: "", label: "— Aucun —" }, ...sousTraitants.map(s => ({ value: s.nom, label: s.nom }))];
  const pOptions = [{ value: "", label: "— Sélectionner —" }, ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom} (${p.habilitation})` }))];

  const now = new Date();
  const filtered = filter === "Tous" ? incidents : incidents.filter(i => i.statut === filter);
  const rawHighlightId = searchHighlight?.id != null ? Number(searchHighlight.id) : null;
  const highlightItemType = searchHighlight?.itemType || null;
  const highlightId =
    rawHighlightId != null && (!highlightItemType || highlightItemType === "incident") ? rawHighlightId : null;
  const searchTerm = searchHighlight?.searchTerm || null;

  useEffect(() => {
    if (highlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId]);

  const handlePgisIncidentReport = async (inc) => {
    setReportBusyId(inc.id);
    try {
      await incidentsApi.sync(incidents);
      const data = await reportsApi.generate("pgis_incident", { security_incident_id: inc.id });
      onToast?.(
        "success",
        reportGenerateSuccessToastMessage(data, "Rapport incident généré — signature DPO requise puis scellement TSA.")
      );
    } catch (error) {
      onToast?.("error", error.message || "Impossible de générer le rapport incident");
    } finally {
      setReportBusyId(null);
    }
  };

  const stats = {
    total: incidents.length,
    ouverts: incidents.filter(i => !["Clôturé","Sans suite"].includes(i.statut)).length,
    critique: incidents.filter(i => i.gravite === "Critique" || i.gravite === "Majeure").length,
    notifies: incidents.filter(i => i.statut === "Notifié CNIL").length,
  };

  return (
    <PageShell>
      <PageHeader
        title="Registre des incidents & violations"
        subtitle="Obligation de notification CNIL sous 72h — art. 33 RGPD"
        actions={
          <button type="button" className="page-shell__btn page-shell__btn--primary" onClick={openNew}>
            + Déclarer un incident
          </button>
        }
      />

      <PageContent>
        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 22 }}>
          {[
            { label: "Total incidents", val: stats.total, color: "#7f1d1d", bg: "#fff1f2", border: "#fecdd3" },
            { label: "En cours", val: stats.ouverts, color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" },
            { label: "Majeurs / Critiques", val: stats.critique, color: "#9d174d", bg: "#fdf2f8", border: "#f9a8d4" },
            { label: "Notifiés CNIL", val: stats.notifies, color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
          ].map(k => (
            <div key={k.label} style={{ background: k.bg, borderRadius: RADIUS.lg, padding: "14px 18px", border: `1.5px solid ${k.border}` }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.val}</div>
              <div style={{ fontSize: 12, color: k.color, marginTop: 3, fontWeight: 600 }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* Alerte 72h */}
        {incidents.filter(i => i.statut === "Détecté" || i.statut === "En cours d'analyse").map(i => {
          const det = new Date(i.dateDetection);
          const heures = Math.round((now - det) / 3600000);
          if (heures < 72 && heures > 0 && !i.dateNotifCnil) return (
            <div key={i.id} style={{ background: "#fef2f2", border: "2px solid #dc2626", borderRadius: RADIUS.lg, padding: "10px 16px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 20, display: "inline-flex", alignItems: "center" }}><MdAccessTime size={20} /></span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 800, color: "#991b1b", fontSize: 13 }}>ALERTE 72H — </span>
                <span style={{ fontSize: 13, color: "#991b1b" }}><strong>{i.titre}</strong> — détecté il y a <strong>{heures}h</strong>. Décision de notification CNIL requise avant {formatDateTimeFr(new Date(det.getTime() + 72 * 3600000))}.</span>
              </div>
            </div>
          );
          return null;
        })}

        {/* Filtres */}
        <div style={{ display: "flex", gap: 6, marginBottom: 16, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 600 }}>Statut :</span>
          {["Tous", ...STATUT_INC].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{ padding: "4px 12px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: filter === s ? "#7f1d1d" : "#fff", color: filter === s ? "#fff" : "#374151", border: `1.5px solid ${filter === s ? "#7f1d1d" : COLORS.border}` }}>{s}</button>
          ))}
        </div>

        {showForm && form && (
          <FormModal
            title={editing ? "Modifier l'incident" : "Déclarer un nouvel incident"}
            headerBackground="#7f1d1d"
            maxWidth={720}
            footer={
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" onClick={() => { setShowForm(false); setForm(null); setEditing(null); }} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Annuler</button>
                <button type="button" onClick={save} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
              </div>
            }
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Titre de l'incident"><Input value={form.titre} onChange={set("titre")} placeholder="Ex : Accès non autorisé à la base emails" /></FieldGroup>
              </div>
              <FieldGroup label="Type d'incident"><SelectField value={form.type} onChange={set("type")} options={TYPE_INC} /></FieldGroup>
              <FieldGroup label="Gravité"><SelectField value={form.gravite} onChange={set("gravite")} options={GRAVITE} /></FieldGroup>
              <FieldGroup label="Statut"><SelectField value={form.statut} onChange={set("statut")} options={STATUT_INC} /></FieldGroup>
              <FieldGroup label="Responsable"><SelectField value={form.responsableId} onChange={set("responsableId")} options={pOptions} /></FieldGroup>
              <FieldGroup label="Date de détection"><Input type="date" value={form.dateDetection} onChange={set("dateDetection")} /></FieldGroup>
              <FieldGroup label="Date notification CNIL"><Input type="date" value={form.dateNotifCnil} onChange={set("dateNotifCnil")} /></FieldGroup>
              <FieldGroup label="Sous-traitant concerné"><SelectField value={form.sousTraitantConcerne} onChange={set("sousTraitantConcerne")} options={stOptions} /></FieldGroup>
              <FieldGroup label="Nb personnes concernées"><Input value={form.nbPersonnesConcernees} onChange={set("nbPersonnesConcernees")} placeholder="Ex : 12000" /></FieldGroup>
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Données impactées"><Input value={form.donneesImpactees} onChange={set("donneesImpactees")} placeholder="Ex : Emails clients, données bancaires…" /></FieldGroup>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Description de l'incident"><Textarea value={form.descriptionIncident} onChange={set("descriptionIncident")} rows={3} placeholder="Décrire les faits, le vecteur d'attaque, l'étendue…" /></FieldGroup>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Mesures prises"><Textarea value={form.mesuresPrises} onChange={set("mesuresPrises")} rows={2} placeholder="Actions immédiates, mesures correctives, plan de remédiation…" /></FieldGroup>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <div style={{ display: "flex", gap: 20 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.delai72h} onChange={setCheck("delai72h")} />
                    <span>Notification CNIL dans les 72h</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.notifPersonnes} onChange={setCheck("notifPersonnes")} />
                    <span>Personnes notifiées (art. 34)</span>
                  </label>
                </div>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Observations / Suivi"><Textarea value={form.observations} onChange={set("observations")} rows={2} placeholder="Suivi, prochaines étapes, retour CNIL…" /></FieldGroup>
              </div>
            </div>
          </FormModal>
        )}

        {/* Liste */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: COLORS.textMuted, fontSize: 14 }}>Aucun incident déclaré.</div>
        ) : filtered.map(inc => {
          const gc = graviteColor[inc.gravite] || graviteColor["Modérée"];
          const sc = statutIncColor[inc.statut] || statutIncColor["Détecté"];
          const resp = personnes.find(p => p.id === inc.responsableId || p.id === Number(inc.responsableId));
          const hc = resp ? (habColor[resp.habilitation] || habColor["Autre"]) : null;
          const det = inc.dateDetection ? new Date(inc.dateDetection) : null;
          const heures = det ? Math.round((now - det) / 3600000) : null;
          const urgent = heures !== null && heures < 72 && !inc.dateNotifCnil && inc.statut !== "Clôturé";
          const isHighlighted = inc.id === highlightId || Number(inc.id) === highlightId;
          return (
            <div
              key={inc.id}
              ref={isHighlighted ? highlightRef : null}
              style={{
                background: isHighlighted ? "#fffbeb" : COLORS.white,
                borderRadius: RADIUS.lg,
                border: `1.5px solid ${isHighlighted ? "#f59e0b" : urgent ? "#dc2626" : COLORS.border}`,
                padding: 18,
                marginBottom: 12,
                boxShadow: isHighlighted ? "0 0 0 2px #fef3c7" : urgent ? "0 0 0 3px #fee2e240" : "0 1px 4px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.2s",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: COLORS.textPrimary, marginBottom: 5 }}><HighlightText text={inc.titre} term={searchTerm} /></div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#f1f5f9", color: "#374151", border: `1px solid ${COLORS.border}`, fontWeight: 600 }}>{inc.type}</span>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: gc.bg, color: gc.text, border: `1px solid ${gc.border}`, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}><MdOutlineFlashOn size={13} /> {inc.gravite}</span>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, fontWeight: 700 }}>{inc.statut}</span>
                    {inc.delai72h && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#d1fae5", color: "#065f46", border: "1px solid #6ee7b7", fontWeight: 700 }}>✓ 72h respectées</span>}
                    {inc.notifPersonnes && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 700 }}>Personnes notifiées</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    title="PDF à faire signer par le DPO (PGIS) puis horodatage TSA"
                    onClick={() => handlePgisIncidentReport(inc)}
                    disabled={reportBusyId === inc.id}
                    style={{
                      padding: "6px 10px",
                      borderRadius: RADIUS.sm,
                      border: "1.5px solid #1d4ed8",
                      background: "#eff6ff",
                      color: "#1d4ed8",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: reportBusyId === inc.id ? "wait" : "pointer",
                      opacity: reportBusyId === inc.id ? 0.75 : 1,
                    }}
                  >
                    {reportBusyId === inc.id ? "PDF…" : "Rapport PDF (DPO)"}
                  </button>
                  <button onClick={() => openEdit(inc)} style={{ padding: `${SPACING.xs}px 9px`, borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdEdit size={14} /></button>
                  <button onClick={() => setIncidents(is => is.filter(i => i.id !== inc.id))} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdDelete size={14} /></button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8 }}><HighlightText text={inc.descriptionIncident} term={searchTerm} /></div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11, alignItems: "center" }}>
                {resp && hc && <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: hc.bg, border: `1.5px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: hc.text }}>{resp.prenom[0]}{resp.nom[0]}</div>
                  <span>{resp.prenom} {resp.nom}</span><HabBadge habilitation={resp.habilitation} />
                </div>}
                {inc.dateDetection && <span style={{ color: COLORS.textSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}><MdCalendarToday size={13} /> Détecté le {formatDateFr(inc.dateDetection)}</span>}
                {inc.dateNotifCnil && <span style={{ color: "#1d4ed8", display: "inline-flex", alignItems: "center", gap: 4 }}><MdApartment size={13} /> CNIL notifiée le {formatDateFr(inc.dateNotifCnil)}</span>}
                {inc.nbPersonnesConcernees && <span style={{ color: "#374151", display: "inline-flex", alignItems: "center", gap: 4 }}><MdGroups size={13} /> {inc.nbPersonnesConcernees} personnes</span>}
                {inc.sousTraitantConcerne && <span style={{ color: "#7c3aed", display: "inline-flex", alignItems: "center", gap: 4 }}><MdBusiness size={13} /> {inc.sousTraitantConcerne}</span>}
              </div>
              {inc.mesuresPrises && <div style={{ marginTop: 8, padding: "6px 10px", background: "#f0fdf4", borderRadius: RADIUS.sm, fontSize: 11, color: "#15803d", display: "inline-flex", alignItems: "center", gap: 4 }}><MdLocalPolice size={13} /> {inc.mesuresPrises}</div>}
            </div>
          );
        })}
      </PageContent>
    </PageShell>
  );
}
